import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useCreateMeterManufacturer, useUpdateMeterManufacturer } from "@features/mx/manufacturer/hooks";
import { useToast } from "@shared/hooks/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { locationJsonService } from "@shared/services/locationService";

interface AddManufacturerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editData?: any;
}

export const AddManufacturerModal: React.FC<AddManufacturerModalProps> = ({
  open,
  onOpenChange,
  editData,
}) => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  const { mutate: createManufacturer } = useCreateMeterManufacturer();
  const { mutate: updateManufacturer } = useUpdateMeterManufacturer();

  const formFields: FormField[] = [
    {
      name: "name",
      label: "Manufacturer Name",
      type: "text",
      required: true,
      placeholder: "Enter manufacturer name",
    },
    {
      name: "country",
      label: "Country",
      type: "select",
      required: true,
      placeholder: "Select country",
      options: locationJsonService.getCountries().map((c) => ({
        label: c.label,
        value: c.label,
      })),
      className: "mt-4",
    },
    {
      name: "websiteUrl",
      label: "Website URL",
      type: "text",
      required: false,
      placeholder: "https://www.example.com",
      className: "mt-4",
    },
    {
      name: "contactNumber",
      label: "Contact Number",
      type: "country-phone",
      required: true,
      placeholder: "Enter contact number",
      className: "mt-4",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Additional information about the manufacturer",
      className: "mt-4",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  useEffect(() => {
    console.log('contactttt', editData)
    if (open) {
      if (editData) {
        form.reset({
          name: editData.name || "",
          country: editData.country || "",
          websiteUrl: editData.websiteUrl || editData.website || "",
          contactNumber: editData.contactNumber || editData.phone || "",
          description: editData.description || "",
        });
      } else {
        form.reset({
          name: "",
          country: "",
          websiteUrl: "",
          contactNumber: "",
          description: "",
        });
      }
    }
  }, [open, editData]);

  const handleSubmit = (data: any) => {
    const payload = {
      remoteUtilityId: Number(remoteUtilityId),
      name: data.name,
      country: data.country,
      websiteUrl: data.websiteUrl,
      contactNumber: data.contactNumber,
      description: data.description,
    };

    if (editData) {
      updateManufacturer(
        {
          manufacturerId: String(editData.id),
          payload: payload as any,
        },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: "Manufacturer updated successfully.",
            });
            queryClient.invalidateQueries({
              queryKey: ["manufacturer", "meterManufacturers"],
            });
            onOpenChange(false);
          },
        }
      );
    } else {
      createManufacturer(payload as any, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Manufacturer added successfully.",
          });
          queryClient.invalidateQueries({
            queryKey: ["manufacturer", "meterManufacturers"],
          });
          onOpenChange(false);
        },
      });
    }
  };

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 mt-6">
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
        Cancel
      </Button>
      <Button type="submit" disabled={!form.formState.isValid}>
        {editData ? "Save Changes" : "Add Manufacturer"}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {editData ? "Edit Manufacturer" : "Add New Manufacturer"}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            config={{
              showSubmitButton: false,
              classes: FIELD_CLASSES,
            }}
            renderCustomFooter={renderCustomFooter}
            gridColumns={1}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default AddManufacturerModal;
